import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

export default function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, number>());
  const previousPath = useRef<string | null>(null);
  const latestLocation = useRef(location);
  latestLocation.current = location;
  const scrollKey = `${location.key}:${location.hash}`;

  const savePosition = () => {
    const next = latestLocation.current;
    if (next.pathname === location.pathname && next.hash !== location.hash) return;
    positions.current.set(scrollKey, window.scrollY);
  };

  useLayoutEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    const samePath = previousPath.current === location.pathname;
    previousPath.current = location.pathname;

    if (location.hash) {
      const frame = window.requestAnimationFrame(() => {
        let id = location.hash.slice(1);
        try {
          id = decodeURIComponent(id);
        } catch {
          // Keep the raw hash when a malformed escape was supplied in a URL.
        }
        const target = document.getElementById(id);
        if (target) target.scrollIntoView({ block: 'start' });
        else window.scrollTo(0, 0);
      });

      return () => {
        window.cancelAnimationFrame(frame);
        savePosition();
      };
    }

    if (navigationType === 'POP') {
      window.scrollTo(0, positions.current.get(scrollKey) ?? 0);
    } else if (!samePath) {
      window.scrollTo(0, 0);
    }

    return () => {
      savePosition();
    };
  }, [location.hash, location.key, location.pathname, navigationType]);

  return null;
}
